import React, { useState, useEffect, useRef } from 'react';
import { 
  DndContext, 
  DragOverlay, 
  closestCorners, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors 
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { createPortal } from 'react-dom';
import { KanbanColumn } from '../components/leads/KanbanColumn';
import { LeadCard } from '../components/leads/LeadCard';
import { Button } from '../components/ui/button';
import { Plus, Filter, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { ConversionModal } from '../components/leads/ConversionModal';
import { NewLeadModal } from '../components/leads/NewLeadModal';
import { LeadDetailModal } from '../components/leads/LeadDetailModal';
import api from '../lib/api';
import { toast } from 'sonner';

import { LEAD_STAGES, getBackendValueByStageId } from '../components/leads/constants';

// Definición de las 7 Columnas Estrictas (Base)
const ALL_COLUMNS = [
  { id: 'new', title: 'Prospecto Nuevo' },
  { id: 'contacted', title: 'Llamada Realizada' },
  { id: 'meeting', title: 'Reunión Agendada' },
  { id: 'proposal', title: 'Propuesta Presentada' },
  { id: 'negotiation', title: 'Negociación/Cierre' },
  { id: 'won', title: 'GANADO' },
  { id: 'lost', title: 'Perdido/Pospuesto' }
];

// Datos Mock Iniciales
const INITIAL_LEADS = [
  { id: '1', name: 'Juan Pérez', company: 'Empresa ACME', product: 'Autos', value: 15000, stage: 'new', updatedAt: new Date().toISOString() },
  { id: '2', name: 'Hospital Santa María', company: 'Grupo Salud', product: 'GMM', value: 450000, stage: 'proposal', updatedAt: new Date().toISOString() },
  { id: '3', name: 'Ana López', company: null, product: 'Vida', value: 25000, stage: 'new', updatedAt: new Date().toISOString() },
  { id: '4', name: 'Constructora del Norte', company: 'Constructora SA', product: 'Daños', value: 120000, stage: 'negotiation', updatedAt: new Date().toISOString() },
  { id: '5', name: 'Pedro Sánchez', company: null, product: 'Autos', value: 8500, stage: 'won', updatedAt: new Date().toISOString() },
  { id: '6', name: 'Roberto Díaz', company: null, product: 'Hogar', value: 4200, stage: 'contacted', updatedAt: new Date().toISOString() },
];

const LeadsBoardOptimized = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Estado para Modal de Conversión
  const [conversionModalOpen, setConversionModalOpen] = useState(false);
  const [leadToConvert, setLeadToConvert] = useState(null);

  // Estado para Modal de Nuevo Lead
  const [newLeadModalOpen, setNewLeadModalOpen] = useState(false);
  
  // Estado para Modal de Detalle
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Estado Filtro Archivados
  const [viewArchived, setViewArchived] = useState(false);
  
  const scrollContainerRef = useRef(null);

  // Cargar leads reales
  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await api.get('/leads');
      // Mapear datos del backend al formato visual esperado
      const mappedLeads = response.data.map(l => ({
        id: l.id,
        name: l.name,
        company: l.company || l.notes?.slice(0, 30), // Fallback si no hay company
        product: l.policy_type || 'Daños', // Fallback
        value: l.estimated_annual_premium || 0,
        stage: mapBackendStageToId(l.stage),
        updatedAt: l.updated_at
      }));
      setLeads(mappedLeads);
    } catch (error) {
      console.error("Error cargando leads:", error);
      toast.error("Error al cargar el pipeline");
    } finally {
      setLoading(false);
    }
  };

  // Handler para creación exitosa
  const handleLeadCreated = (newLeadData) => {
    // Recargar leads para asegurar consistencia con backend
    fetchLeads();
    setNewLeadModalOpen(false);
  };

  // Helper para mapear etapas del backend (Strings) a IDs del frontend
  const mapBackendStageToId = (backendStage) => {
    const stage = LEAD_STAGES.find(s => s.backendValue === backendStage || s.label === backendStage);
    return stage ? stage.id : 'new';
  };
  
  // Helper inverso
  const mapIdToBackendStage = (id) => {
    return getBackendValueByStageId(id);
  };

  // Detectar móvil
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Sensores para Dnd-Kit
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }, // Prevenir drag accidental al hacer click
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // --- Lógica de Drag & Drop ---

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over) {
      setActiveId(null);
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    // Encontrar el lead y su columna actual
    const activeLead = leads.find(l => l.id === activeId);
    
    // Si 'over' es una columna (contenedor vacío o drop directo)
    const isOverColumn = ALL_COLUMNS.some(col => col.id === overId);
    // Si 'over' es otro lead
    const overLead = leads.find(l => l.id === overId);
    
    let newStage = null;

    if (isOverColumn) {
      newStage = overId;
    } else if (overLead) {
      newStage = overLead.stage;
    }

    if (newStage && activeLead.stage !== newStage) {
      // INTERCEPTAR: Si se mueve a GANADO, abrir modal antes de cambiar estado
      if (newStage === 'won') {
        setLeadToConvert(activeLead);
        setConversionModalOpen(true);
        // NO cambiamos el estado local todavía
        setActiveId(null);
        return;
      }

      // Cambio normal de columna
      // 1. Optimistic Update
      const oldStage = activeLead.stage;
      setLeads(leads.map(lead => 
        lead.id === activeId ? { ...lead, stage: newStage } : lead
      ));

      // 2. API Call
      api.put(`/leads/${activeId}/stage`, { stage: mapIdToBackendStage(newStage) })
        .catch(err => {
          console.error("Error moviendo lead:", err);
          toast.error("Error al actualizar etapa");
          // Revertir
          setLeads(leads.map(lead => 
            lead.id === activeId ? { ...lead, stage: oldStage } : lead
          ));
        });

    } else if (activeLead.stage === newStage) {
      // Reordenamiento en la misma columna (opcional, por ahora no persiste orden)
    }

    setActiveId(null);
  };

  // --- Lógica para Móvil (Movimiento sin Drag) ---
  const handleMoveToStage = (leadId, targetStageId) => {
    // INTERCEPTAR: Si se mueve a GANADO
    if (targetStageId === 'won') {
      const lead = leads.find(l => l.id === leadId);
      setLeadToConvert(lead);
      setConversionModalOpen(true);
      return;
    }

    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;
    const oldStage = lead.stage;

    // Optimistic
    setLeads(prev => prev.map(l => 
      l.id === leadId ? { ...l, stage: targetStageId } : l
    ));
    
    // API
    api.put(`/leads/${leadId}/stage`, { stage: mapIdToBackendStage(targetStageId) })
      .catch(err => {
        console.error("Error moviendo lead:", err);
        toast.error("Error al actualizar etapa");
        // Revertir
        setLeads(prev => prev.map(l => 
          l.id === leadId ? { ...l, stage: oldStage } : l
        ));
      });
    
    // Auto-scroll a la columna destino
    const columnIndex = ALL_COLUMNS.findIndex(c => c.id === targetStageId);
    if (scrollContainerRef.current && columnIndex !== -1) {
      const cardWidth = window.innerWidth * 0.85; // Aproximado del ancho de tarjeta
      // Ajuste simple: scroll al índice * ancho + margen
      // Mejor: usar scrollIntoView si tuviéramos refs a cada columna, 
      // pero con scrollLeft es suficiente para MVP
      scrollContainerRef.current.scrollTo({
        left: columnIndex * (cardWidth + 16), // 16px gap
        behavior: 'smooth'
      });
    }

    if (targetStageId === 'won') {
      console.log("TRIGGER CONVERSION MODAL (MOBILE)", leadId);
    }
  };

  // --- Lógica de Confirmación de Conversión ---
  const handleConversionConfirm = async (conversionData) => {
    try {
      await api.post(`/leads/${leadToConvert.id}/win-deal`, conversionData);
      
      // Actualizar estado local optimista
      setLeads(prev => prev.map(lead => 
        lead.id === leadToConvert.id ? { ...lead, stage: 'won' } : lead
      ));
      
      setConversionModalOpen(false);
      setLeadToConvert(null);
      // Opcional: Recargar leads para asegurar sincronización
      // fetchLeads(); 
    } catch (error) {
      console.error("Error al ganar venta:", error);
      toast.error("Error al registrar la venta");
      throw error; // Re-lanzar para que el modal maneje el estado de carga
    }
  };

  // --- Lógica de Eliminación de Lead ---
  const handleDeleteLead = async (leadId) => {
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success("Prospecto eliminado exitosamente");
      // Actualizar estado local
      setLeads(prev => prev.filter(l => l.id !== leadId));
    } catch (error) {
      console.error("Error eliminando lead:", error);
      toast.error("Error al eliminar el prospecto");
    }
  };

  // Filtrado de leads por columna
  const getLeadsByStage = (stageId) => {
    return leads.filter(l => {
      // Coincidencia de etapa
      if (l.stage !== stageId) return false;
      
      // Filtro de Vista
      if (viewArchived) {
        return l.stage === 'lost';
      } else {
        return l.stage !== 'lost';
      }
    });
  };

  // Handler para click en tarjeta
  const handleCardClick = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      setSelectedLead(lead);
      setDetailModalOpen(true);
    }
  };

  // Columnas visibles
  const visibleColumns = viewArchived
    ? ALL_COLUMNS.filter(c => c.id === 'lost') 
    : ALL_COLUMNS.filter(c => c.id !== 'lost');

  // Lead activo para overlay
  const activeLead = activeId ? leads.find(l => l.id === activeId) : null;

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col bg-slate-50 overflow-hidden">
      
      {/* --- HEADER --- */}
      <div className="flex-none px-6 py-4 bg-white border-b border-slate-200 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pipeline de Ventas</h1>
          <p className="text-sm text-slate-500 hidden sm:block">Gestiona tus oportunidades desde prospecto hasta cierre</p>
        </div>
        <div className="flex gap-2">
          <div className="relative hidden md:block">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input className="pl-9 w-64 h-9 bg-slate-50" placeholder="Buscar lead..." />
          </div>
          
          <Button 
            variant={viewArchived ? "secondary" : "outline"} 
            size="sm" 
            className="hidden sm:flex gap-2"
            onClick={() => setViewArchived(!viewArchived)}
          >
            <Filter size={16} /> 
            {viewArchived ? 'Ver Activos' : 'Ver Archivados'}
          </Button>

          <Button 
            onClick={() => setNewLeadModalOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 gap-2 shadow-sm"
          >
            <Plus size={18} /> <span className="hidden sm:inline">Nuevo Lead</span><span className="sm:hidden">Nuevo</span>
          </Button>
        </div>
      </div>

      {/* --- MOBILE INDICATORS (Dots) --- */}
      {isMobile && (
        <div className="flex justify-center gap-1.5 py-2 bg-slate-50 border-b border-slate-100">
          {ALL_COLUMNS.map((col, idx) => (
            <div 
              key={col.id}
              className={`h-1.5 rounded-full transition-all ${
                // Lógica visual simple: resaltar todos por ahora, idealmente detectar scroll
                'w-1.5 bg-slate-300' 
              }`} 
              title={col.title}
            />
          ))}
          <span className="text-[10px] text-slate-400 ml-2">Desliza para ver etapas &rarr;</span>
        </div>
      )}

      {/* --- BOARD AREA --- */}
      <DndContext 
        sensors={sensors} 
        collisionDetection={closestCorners} 
        onDragStart={handleDragStart} 
        onDragEnd={handleDragEnd}
      >
        <div 
          ref={scrollContainerRef}
          className={`
            flex-1 p-4 overflow-x-auto overflow-y-hidden
            flex gap-4
            ${isMobile ? 'snap-x snap-mandatory' : ''}
          `}
        >
          {visibleColumns.map(column => (
            <KanbanColumn
              key={column.id}
              id={column.id}
              title={column.title}
              leads={getLeadsByStage(column.id)}
              isMobile={isMobile}
              onMoveToStage={handleMoveToStage}
              onCardClick={handleCardClick}
              onDelete={handleDeleteLead}
            />
          ))}
        </div>

        {/* Drag Overlay (Solo visible al arrastrar) */}
        {createPortal(
          <DragOverlay>
            {activeLead ? (
              <div className="rotate-2 cursor-grabbing opacity-90 scale-105">
                 <LeadCard lead={activeLead} isMobile={false} />
              </div>
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>

      <ConversionModal 
        isOpen={conversionModalOpen}
        onClose={() => setConversionModalOpen(false)}
        lead={leadToConvert}
        onConfirm={handleConversionConfirm}
      />

      <NewLeadModal
        isOpen={newLeadModalOpen}
        onClose={() => setNewLeadModalOpen(false)}
        onSuccess={handleLeadCreated}
      />

      <LeadDetailModal
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        lead={selectedLead}
        onUpdate={fetchLeads}
        onDelete={(id) => {
          setLeads(prev => prev.filter(l => l.id !== id));
        }}
      />
    </div>
  );
};

export default LeadsBoardOptimized;
